import { Stack, Container, Typography } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import {
  BuySellTxType,
  TradeTxFilters,
  TradeTxFilterKey,
  TradeTxFilterValue,
} from 'src/type/trade-tx';
import { isAfter } from 'src/utils/format-time';
import { useInView } from 'react-intersection-observer';
import { useBoolean } from 'src/hooks/use-boolean';
import { toastError } from 'src/utils/format-error';
import { Pagination } from 'src/type/utils';
import { fParams, fDateRange, ParamsType } from 'src/utils/format-params';
import axios, { endpoints } from 'src/utils/axios';
import LoadingContainer from 'src/components/ui-kit/loading-container';
import CenterStack from 'src/components/ui-kit/center-stack';
import { TransactionType } from 'src/type/transaction';
import EmptyContent from 'src/components/empty-content';
import useProductOptions from 'src/hooks/use-product-options';
import TradeTxCard from '../trade-tx-card';
import TradeTxFiltersToolbar from '../trade-tx-filters-toolbar';
import TradeTxSkeleton from '../trade-tx-skeleton';

const defaultFilters: TradeTxFilters = {
  name: null,
  type: 'all',
  productId: 'all',
  startDate: null,
  endDate: null,
};

export default function TradeTxView() {
  const [scrollTrigger, isInView] = useInView();
  const [filters, setFilters] = useState(defaultFilters);
  const [tableData, setTableData] = useState(Array<BuySellTxType>());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);

  const initialize = useBoolean(true);
  const { productOptions } = useProductOptions(false, filters.name?.value);

  const dateError = isAfter(filters.startDate?.toDate(), filters.endDate?.toDate());

  useEffect(() => {
    load();
  }, [filters.type, filters.startDate, filters.endDate, filters.productId, filters.name?.value]);

  useEffect(() => {
    if (isInView && hasMoreData) {
      loadMore();
    }
  }, [isInView, hasMoreData]);

  const load = async () => {
    try {
      initialize.onTrue();
      const data = await getTransaction({
        page: 1,
        lastDocId: '',
      });

      setTableData(data);
      setCurrentPage(1);
      setHasMoreData(true);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  };

  const loadMore = async () => {
    try {
      if (hasMoreData && !initialize.value) {
        const data = await getTransaction({
          page: currentPage + 1,
          lastDocId: tableData[tableData.length - 1]?.id || '',
        });
        if (data.length === 0) {
          setHasMoreData(false);
        }
        setTableData((prev) => [...prev, ...data]);
        setCurrentPage((prev) => prev + 1);
      }
    } catch (error) {
      toastError(error);
    }
  };

  const getTransaction = async (params: ParamsType) => {
    const _params = {
      ...params,
      limit: 30,
      userId: filters.name?.value,
      productId: filters.productId === 'all' ? undefined : filters.productId,
      type:
        filters.type === 'all' ? `${TransactionType.BUY},${TransactionType.SELL}` : filters.type,
      ...fDateRange(filters.startDate, filters.endDate),
    };

    const { data } = await axios.get<Pagination<BuySellTxType>>(endpoints.transactions.get, {
      params: fParams(_params),
    });
    return data.results;
  };

  const handleFilters = useCallback((name: TradeTxFilterKey, value: TradeTxFilterValue) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  return (
    <Container>
      <Stack spacing={3}>
        <Typography variant="h4" gutterBottom>
          การซื้อขาย
        </Typography>
        <TradeTxFiltersToolbar
          filters={filters}
          onFilters={handleFilters}
          dateError={dateError}
          productOptions={productOptions}
        />
        <LoadingContainer loading={initialize.value} fitContent sx={{ height: '40vh' }}>
          {/* <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={3}>
              <TradeTxSummaryCard
                title="ยอดซื้อทอง"
                value={0}
                unit="KG"
                series={[
                  { label: '0%', value: '0 KG' },
                  { label: '0%', value: '0 KG' },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TradeTxSummaryCard
                title="ยอดขายทอง"
                value={0}
                unit="KG"
                series={[
                  { label: '0%', value: '0 KG' },
                  { label: '0%', value: '0 KG' },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TradeTxSummaryCard
                title="ส่วนต่าง"
                value={0}
                unit="KG"
                series={[
                  { label: '0%', value: '0 KG' },
                  { label: '0%', value: '0 KG' },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TradeTxSummaryCard
                title="ค่าธรรมเนียม"
                value={0}
                unit="KG"
                series={[
                  { label: 'ซื้อ', value: '0 เครดิต' },
                  { label: 'ขาย', value: '0 เครดิต' },
                ]}
              />
            </Grid>
          </Grid> */}

          <Stack spacing={2}>
            {tableData.map((tradeTx) => (
              <TradeTxCard key={tradeTx.id} tradeTx={tradeTx} />
            ))}

            {!tableData.length && <EmptyContent title="ไม่พบข้อมูล" sx={{ mt: 4 }} />}

            {hasMoreData ? (
              <div ref={scrollTrigger}>
                <Stack spacing={2}>
                  <TradeTxSkeleton loading rows={2} />
                </Stack>
              </div>
            ) : (
              <CenterStack mt={3}>
                {!!tableData.length && (
                  <Typography variant="caption" color="text.secondary">
                    ทั้งหมด {tableData.length} รายการ
                  </Typography>
                )}
              </CenterStack>
            )}
          </Stack>
        </LoadingContainer>
      </Stack>
    </Container>
  );
}
